import type {
  LoginResponse,
  LatestResponse,
  ListResponse,
  ChartResponse,
  SettingsResponse,
  DeviceListResponse,
  CaptureResponse,
  CaptureStatusResponse,
} from '@/types/api';

const BASE_URL = 'https://tof.eemo.co.kr/api';

async function fetchApi<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function postApi<T>(url: string, body: Record<string, unknown>): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

export const cropApi = {
  /**
   * 장비 조회
   * 4자리 이하 → GET /api/api_login.php?device_suffix=3C7C
   * 5자리 이상 → GET /api/api_login.php?device_id=3C6BC3AA3C7C
   */
  login: (input: string): Promise<LoginResponse> => {
    const trimmed = input.trim().toUpperCase();
    if (trimmed.length <= 4) {
      return fetchApi<LoginResponse>(
        `${BASE_URL}/api_login.php?device_suffix=${encodeURIComponent(trimmed)}`
      );
    }
    return fetchApi<LoginResponse>(
      `${BASE_URL}/api_login.php?device_id=${encodeURIComponent(trimmed)}`
    );
  },

  /**
   * 최신 촬영 데이터 1건 조회
   * GET /api/api_latest.php?device_id=GF-CROP-001
   */
  getLatest: (deviceId: string): Promise<LatestResponse> =>
    fetchApi<LatestResponse>(
      `${BASE_URL}/api_latest.php?device_id=${encodeURIComponent(deviceId)}`
    ),

  /**
   * 히스토리 목록 조회 (페이징 + 날짜 필터)
   * GET /api/api_list.php?device_id=GF-CROP-001&page=1&limit=20&sdate=2026-04-16&edate=2026-04-18
   */
  getList: (
    deviceId: string,
    page: number,
    limit: number,
    sdate?: string,
    edate?: string
  ): Promise<ListResponse> => {
    let url = `${BASE_URL}/api_list.php?device_id=${encodeURIComponent(deviceId)}&page=${page}&limit=${limit}`;
    if (sdate) url += `&sdate=${encodeURIComponent(sdate)}`;
    if (edate) url += `&edate=${encodeURIComponent(edate)}`;
    return fetchApi<ListResponse>(url);
  },

  /**
   * 차트용 데이터 조회 (최근 N건)
   * GET /api/api_chart.php?device_id=GF-CROP-001&cnt=30
   */
  getChart: (deviceId: string, cnt = 30): Promise<ChartResponse> =>
    fetchApi<ChartResponse>(
      `${BASE_URL}/api_chart.php?device_id=${encodeURIComponent(deviceId)}&cnt=${cnt}`
    ),

  /**
   * 장비 원격 설정 전송
   * GET /api/api_setting.php?device_id=...&timer1_time=...&timer1_duration=...&...
   */
  sendSettings: (params: {
    deviceId: string;
    timer1Time: string;
    timer1Duration: number;
    timer2Time: string;
    timer2Duration: number;
    housingHeight: number;
    rgb1InitAngle: number;
    rgb2InitAngle: number;
  }): Promise<{ result: string }> => {
    const searchParams = new URLSearchParams({
      device_id: params.deviceId,
      timer1_time: params.timer1Time,
      timer1_duration: String(params.timer1Duration),
      timer2_time: params.timer2Time,
      timer2_duration: String(params.timer2Duration),
      housing_height: String(params.housingHeight),
      rgb1_angle: String(params.rgb1InitAngle),
      rgb2_angle: String(params.rgb2InitAngle),
    });
    return fetchApi<{ result: string }>(
      `${BASE_URL}/api_setting.php?${searchParams}`
    );
  },

  /**
   * 현재 설정값 조회
   * GET /api/api_status.php?device_id=GF-CROP-001&mode=get
   */
  getSettings: (deviceId: string): Promise<SettingsResponse> =>
    fetchApi<SettingsResponse>(
      `${BASE_URL}/api_status.php?device_id=${encodeURIComponent(deviceId)}&mode=get`
    ),

  /**
   * 장비 목록 조회
   * GET /api/api_device_list.php
   */
  getDeviceList: (): Promise<DeviceListResponse> =>
    fetchApi<DeviceListResponse>(`${BASE_URL}/api_device_list.php`),

  /**
   * 수동 촬영 명령 전송
   * POST /api/api_capture.php
   */
  sendCapture: (deviceId: string): Promise<CaptureResponse> =>
    postApi<CaptureResponse>(`${BASE_URL}/api_capture.php`, { device_id: deviceId }),

  /**
   * 촬영 상태 확인 (폴링)
   * GET /api/api_capture_status.php?device_id=xxx
   */
  getCaptureStatus: (deviceId: string): Promise<CaptureStatusResponse> =>
    fetchApi<CaptureStatusResponse>(
      `${BASE_URL}/api_capture_status.php?device_id=${encodeURIComponent(deviceId)}`
    ),
};